// The one place a public booking-widget URL is constructed.
//
// This existed in three copies that had drifted apart: the sidebar built the
// path form (/t/<tenant>/?bookingFlow=...) while the Booking Flows page built
// the query form (/?tenant=...&bookingFlow=...), so "Copy link" and "Share
// booking link" handed out different URLs for the same flow. The widget accepts
// both, which is exactly why nobody noticed.
//
// Query form is canonical. Keep it that way — the embed snippet, the share
// card, and the sidebar must all produce a byte-identical URL for a given flow,
// or support conversations start with "which link did you use?".

const DEFAULT_WEB_EMBED_URL = "https://book.joshyspeed.com";

let warnedAboutFallback = false;

/**
 * Widget host every link is built from. `VITE_WEB_EMBED_URL` is the source of
 * truth and must be set per deployment — the value is baked in at build time,
 * so changing it in Vercel requires a redeploy.
 *
 * The fallback is the PRODUCTION WIDGET host, which is only correct for the
 * production admin. Anywhere else it is silently wrong in the most expensive
 * way available: staging's "Copy link" / "Preview" / "Share booking link" all
 * handed out production URLs, so testing a staging-only feature drove real
 * traffic to production and left staging's tables empty with nothing failing.
 * Hence the warning — an unset variable should be visible, not inferred later
 * from missing rows.
 */
function baseUrl(): string {
  const configured = import.meta.env["VITE_WEB_EMBED_URL"];

  if (!configured && !warnedAboutFallback) {
    warnedAboutFallback = true;
    // Not gated on DEV: the case worth catching is a deployed build, and the
    // fallback is correct on localhost anyway only by coincidence.
    console.warn(
      `[bookingLinks] VITE_WEB_EMBED_URL is not set — every booking link on ` +
        `this admin will point at ${DEFAULT_WEB_EMBED_URL}. Set it to this ` +
        `environment's widget host and redeploy.`,
    );
  }

  // Note the fallback is the WIDGET host, not the admin host. It briefly
  // pointed at cal.feeble.io, which meant every share link led back into the
  // admin app whenever the env var was unset.
  return (configured || DEFAULT_WEB_EMBED_URL).replace(/\/+$/, "");
}

/** The widget host links are currently built from. For display/diagnostics. */
export function getWebEmbedBaseUrl(): string {
  return baseUrl();
}

export interface BookingLinkOptions {
  tenantSlug: string;
  flowSlug: string;
  /**
   * Marks the load as an internal admin preview, which suppresses analytics in
   * the widget (see web-embed/utils/ga.ts).
   *
   * ONLY for links we open ourselves. Never add it to a URL a real visitor
   * receives — a copied link carrying preview=1 would silently stop counting
   * that booker.
   */
  preview?: boolean;
}

/** Public URL for a booking flow. The canonical shape. */
export function buildBookingUrl({
  tenantSlug,
  flowSlug,
  preview = false,
}: BookingLinkOptions): string {
  const params = new URLSearchParams({
    tenant: tenantSlug,
    bookingFlow: flowSlug,
  });
  if (preview) params.set("preview", "1");
  return `${baseUrl()}/?${params}`;
}

/** Iframe snippet for embedding the widget on a customer's site. */
export function buildEmbedCode(options: BookingLinkOptions): string {
  // Embeds are what real visitors load, so they must never carry preview=1.
  const src = buildBookingUrl({ ...options, preview: false });
  return `<iframe
  src="${src}"
  width="100%"
  height="650"
  frameborder="0"
  style="border: none; border-radius: 12px;"
></iframe>`;
}
